import './styles.scss'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Field } from 'react-final-form'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import { classNames } from 'primereact/utils'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'
import DialogModal from 'Components/UIComponents/DialogModal'
import { ButtonComponent } from 'Components/UIComponents'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'

// Reusable Text Input Field Component
const TextInputField = ({ input, name, value, onChange, label, placeholder, isRequired, testId, meta, t, className }) => {
  const isFormFieldValid = (meta) => !!(meta.touched && meta.error)
  const getFormErrorMessage = (meta) => isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>

  return (
    <div className="field">
      <span className="p-float-label">
        <InputText {...input} id={name} name={name} value={value} onChange={(e) => onChange(name, e.target.value)} placeholder={placeholder} className={classNames({ 'p-invalid': isFormFieldValid(meta) }, isFormFieldValid(meta) ? 'p-invalid-hover' : 'form-fields', className)} data-testid={testId} />
        <label htmlFor={name} className={classNames({ 'p-error': isFormFieldValid(meta) })}>
          {label} {isRequired && <span className="span-text-color">*</span>}
        </label>
      </span>
      <div className="error-message">{getFormErrorMessage(meta)}</div>
    </div>
  )
}

// Reusable Month Year Calendar Field Component
const MonthYearCalendarField = ({ input, name, value, label, isRequired, testId, meta, t, handleChange }) => {
  const isFormFieldValid = (meta) => !!(meta.touched && meta.error)
  const getFormErrorMessage = (meta) => isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>

  const handleCalendarChange = (e) => {
    const selectedDate = e.value

    if (selectedDate) {
      // Set to first day of the selected month
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)

      input.onChange(date)

      if (handleChange) {
        handleChange(name, date)
      }
    } else {
      input.onChange(null)
      if (handleChange) {
        handleChange(name, null)
      }
    }
  }

  return (
    <div className="field">
      <span className="p-float-label">
        <Calendar {...input} id={name} name={name} value={value} onChange={handleCalendarChange} view="month" dateFormat="mm/yy" showIcon className={classNames({ 'p-invalid': isFormFieldValid(meta) }, isFormFieldValid(meta) ? 'p-invalid-hover' : 'form-fields')} data-testid={testId} />
        <label htmlFor={name} className={classNames({ 'p-error': isFormFieldValid(meta) })}>
          {label} {isRequired && <span className="span-text-color">*</span>}
        </label>
      </span>
      <div className="error-message">{getFormErrorMessage(meta)}</div>
    </div>
  )
}

// Reusable Country Dropdown Field Component
const CountryDropdownField = ({ input, name, value, onChange, label, placeholder, isRequired, testId, meta, countries, countryOptionTemplate, selectedCountryTemplate, t, valueTransform }) => {
  const isFormFieldValid = (meta) => !!(meta.touched && meta.error)
  const getFormErrorMessage = (meta) => isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>

  const displayValue = valueTransform ? valueTransform(value) : value

  return (
    <div className="field">
      <span className="p-float-label">
        <Dropdown
          {...input}
          id={name}
          name={name}
          data-testid={testId}
          value={displayValue}
          onChange={onChange}
          options={countries}
          optionLabel="name"
          optionValue="id"
          filter
          placeholder={placeholder}
          itemTemplate={countryOptionTemplate}
          valueTemplate={selectedCountryTemplate}
          className={classNames(
            'dropdown-field',
            {
              'p-invalid': isFormFieldValid(meta)
            },
            isFormFieldValid(meta) ? 'p-invalid-hover' : 'form-fields'
          )}
        />
        <label htmlFor={name} className={classNames({ 'p-error': isFormFieldValid(meta) })}>
          {label} {isRequired && <span className="span-text-color">*</span>}
        </label>
      </span>
      <div className="error-message">{getFormErrorMessage(meta)}</div>
    </div>
  )
}

function FreightRates({ setLoading }) {
  const { t } = useTranslation()

  const DEFAULT_FORM_DATA = {
    loadingPortCountry: 102, // India default - Loading Port Country (int)
    destinationPortCountry: '102', // India default - Destination Port Country (string)
    monthYear: null, // Month and Year
    loadingPort: '',
    destinationPort: '',
    freightRate: '',
    transitPeriod: '',
    others: '',
    freightRateFrom: '',
    freightRateTo: ''
  }

  const [freightRatesData, setFreightRatesData] = useState(DEFAULT_FORM_DATA)
  const [formKey, setFormKey] = useState(0) // Key to force form remount
  const [countries, setCountries] = useState([])
  const [showDialog, setShowDialog] = useState({
    showModal: false,
    modalType: '',
    message: '',
    onHide: () => {},
    onRetry: () => {}
  })

  useEffect(() => {
    fetchCountries()
  }, [])

  const fetchCountries = async () => {
    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getAllCountries()

      setCountries(response.data || [])
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: fetchCountries,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const countryOptionTemplate = (option) => {
    if (!option) return null

    return (
      <div className="dropdown-selector">
        <img src={option.flagsUrl} alt="flag" className="flag-icon" />
        <div className="currency-details">
          <div className="currency-code">{option.name}</div>
        </div>
      </div>
    )
  }

  const selectedCountryTemplate = (option) => {
    if (!option) return null
    const selectedCountry = typeof option === 'object' ? option : countries.find((c) => c.id === option)

    if (!selectedCountry || !selectedCountry.flagsUrl) return option?.name || ''

    return (
      <div className="dropdown-selector">
        <img src={selectedCountry.flagsUrl} alt="flag" className="flag-icon" />
        <div className="currency-details">
          <div className="currency-code">{selectedCountry.name}</div>
        </div>
      </div>
    )
  }

  const handleChange = useCallback((name, value) => {
    setFreightRatesData((prevData) => ({
      ...prevData,
      [name]: value
    }))
  }, [])

  const handleLoadingPortCountryChange = useCallback(
    (e) => {
      handleChange('loadingPortCountry', e.value)
    },
    [handleChange]
  )

  const handleDestinationCountryChange = useCallback(
    (e) => {
      const countryId = e.value ? e.value.toString() : ''

      handleChange('destinationPortCountry', countryId)
    },
    [handleChange]
  )

  const onSubmit = (data, form) => {
    setFreightRatesData(data)
    // Reset form to clear validation state
    if (form) {
      form.restart()
    }
  }

  // eslint-disable-next-line
  const buildPayload = () => {
    // Format monthYear as "YYYY-MM" or similar format
    let formattedMonthYear = ''

    if (freightRatesData.monthYear) {
      const date = new Date(freightRatesData.monthYear)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')

      formattedMonthYear = `${year}-${month}`
    }

    return {
      baseCountry: parseInt(freightRatesData.loadingPortCountry, 10),
      destinationPortCountry: String(freightRatesData.destinationPortCountry),
      monthYear: formattedMonthYear,
      loadingPort: String(freightRatesData.loadingPort),
      destinationPort: String(freightRatesData.destinationPort),
      freightRate: String(freightRatesData.freightRate),
      transitPeriod: String(freightRatesData.transitPeriod),
      others: freightRatesData.others,
      freightRateFrom: String(freightRatesData.freightRateFrom),
      freightRateTo: String(freightRatesData.freightRateTo),
      createdBy: 0,
      createdOn: '2026-02-12T06:01:26.221Z',
      isAdmVerified: true,
      admIdUser: 0,
      admApproveDtm: '2026-02-12T06:01:26.221Z',
      isSAVerified: true,
      sAIdUser: 0,
      saApproveDtm: '2026-02-12T06:01:26.221Z',
      admRejectRemark: 'string',
      sadmRejectRemark: 'string',
      activeStatus: true
    }
  }

  const submitFreightRates = async () => {
    setLoading(true)
    handleDismiss()

    const payload = buildPayload()

    try {
      const response = await apiAdapterCoffeeWeb.postAddFreightRatesData(payload)

      if (response?.data?.statusCode === 200 || response?.status === 200) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('FREIGHT_RATES_ADDED_SUCCESSFULLY'),
          onHide: handleSuccess
        })
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('COULD_NOT_ADD_FREIGHT_RATES'),
        onRetry: submitFreightRates,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSuccess = () => {
    setShowDialog({ ...showDialog, showModal: false })
    setFreightRatesData(DEFAULT_FORM_DATA)
    // Force form remount to clear all validation errors
    setFormKey((prevKey) => prevKey + 1)
  }

  const handleDismiss = () => {
    if (showDialog.modalType === 'success') {
      handleSuccess()
    } else {
      setShowDialog({ ...showDialog, showModal: false })
    }
  }

  // Field configuration for validation
  const fieldConfig = {
    loadingPortCountry: { errorKey: 'LOADING_PORT_COUNTRY_IS_REQUIRED' },
    destinationPortCountry: { errorKey: 'DESTINATION_PORT_COUNTRY_IS_REQUIRED' },
    monthYear: { errorKey: 'MONTH_YEAR_IS_REQUIRED' },
    loadingPort: { errorKey: 'LOADING_PORT_IS_REQUIRED' },
    destinationPort: { errorKey: 'DESTINATION_PORT_IS_REQUIRED' },
    freightRate: { errorKey: 'FREIGHT_RATE_IS_REQUIRED' },
    transitPeriod: { errorKey: 'TRANSIT_PERIOD_IS_REQUIRED' },
    others: { errorKey: 'OTHERS_IS_REQUIRED' },
    freightRateFrom: { errorKey: 'FREIGHT_RATE_FROM_IS_REQUIRED' },
    freightRateTo: { errorKey: 'FREIGHT_RATE_TO_IS_REQUIRED' }
  }

  const validate = (data) => {
    const errors = {}

    Object.keys(fieldConfig).forEach((fieldName) => {
      if (!data[fieldName]) {
        const config = fieldConfig[fieldName]

        errors[fieldName] = t(config.errorKey)
      }
    })

    return errors
  }

  // Field definitions for rendering
  const formFieldsConfig = useMemo(
    () => [
      {
        name: 'loadingPortCountry',
        type: 'countryDropdown',
        labelKey: 'LOADING_PORT_COUNTRY',
        placeholderKey: 'SELECT_LOADING_PORT_COUNTRY',
        testId: 'loading-port-country',
        isRequired: true
      },
      {
        name: 'destinationPortCountry',
        type: 'countryDropdown',
        labelKey: 'DESTINATION_PORT_COUNTRY',
        placeholderKey: 'SELECT_DESTINATION_PORT_COUNTRY',
        testId: 'destination-port-country',
        valueTransform: (val) => (val ? parseInt(val, 10) : ''),
        isRequired: true
      },
      {
        name: 'monthYear',
        type: 'monthYearCalendar',
        labelKey: 'MONTH_YEAR',
        placeholderKey: '',
        testId: 'month-year',
        isRequired: true
      },
      {
        name: 'loadingPort',
        type: 'text',
        labelKey: 'LOADING_PORT',
        placeholderKey: '',
        testId: 'loading-port',
        isRequired: true
      },
      {
        name: 'destinationPort',
        type: 'text',
        labelKey: 'DESTINATION_PORT',
        placeholderKey: '',
        testId: 'destination-port',
        isRequired: true
      },
      {
        name: 'freightRate',
        type: 'text',
        labelKey: 'FREIGHT_RATE',
        placeholderKey: '',
        testId: 'freight-rate',
        isRequired: true
      },
      {
        name: 'transitPeriod',
        type: 'text',
        labelKey: 'TRANSIT_PERIOD',
        placeholderKey: 'EG_30_DAYS_OR_NO_SERVICE',
        testId: 'transit-period',
        isRequired: true
      },
      {
        name: 'others',
        type: 'text',
        labelKey: 'OTHERS',
        placeholderKey: '',
        testId: 'others',
        isRequired: true
      },
      {
        name: 'freightRateFrom',
        type: 'text',
        labelKey: 'FREIGHT_RATE_FROM',
        placeholderKey: '',
        testId: 'freight-rate-from',
        isRequired: true
      },
      {
        name: 'freightRateTo',
        type: 'text',
        labelKey: 'FREIGHT_RATE_TO',
        placeholderKey: '',
        testId: 'freight-rate-to',
        isRequired: true
      }
    ],
    []
  )

  const isButtonDisabled = useMemo(() => !freightRatesData.loadingPortCountry || !freightRatesData.monthYear || !freightRatesData.destinationPortCountry || !freightRatesData.loadingPort || !freightRatesData.destinationPort || !freightRatesData.freightRate || !freightRatesData.transitPeriod || !freightRatesData.others || !freightRatesData.freightRateFrom || !freightRatesData.freightRateTo, [freightRatesData])

  return (
    <LayoutContainer title={t('FREIGHT_RATES')} breadCrumbsTitle={'FreightRates'}>
      <div className="add-freight-rates-form" data-testid="add-freight-rates">
        <Form
          key={formKey}
          onSubmit={onSubmit}
          initialValues={freightRatesData}
          validate={validate}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} className="p-fluid">
              {formFieldsConfig.map((fieldConfig) => {
                const fieldValue = freightRatesData[fieldConfig.name]
                const label = t(fieldConfig.labelKey) || fieldConfig.labelKey.replace(/_/g, ' ')
                const placeholder = fieldConfig.placeholderKey ? t(fieldConfig.placeholderKey) || fieldConfig.placeholderKey.replace(/_/g, ' ') : ''

                if (fieldConfig.type === 'countryDropdown') {
                  const onChangeHandler = fieldConfig.name === 'loadingPortCountry' ? handleLoadingPortCountryChange : handleDestinationCountryChange

                  return <Field key={fieldConfig.name} name={fieldConfig.name} render={({ input, meta }) => <CountryDropdownField input={input} name={fieldConfig.name} value={fieldValue} onChange={onChangeHandler} label={label} placeholder={placeholder} isRequired={fieldConfig.isRequired} testId={fieldConfig.testId} meta={meta} countries={countries} countryOptionTemplate={countryOptionTemplate} selectedCountryTemplate={selectedCountryTemplate} valueTransform={fieldConfig.valueTransform} t={t} />} />
                }

                if (fieldConfig.type === 'monthYearCalendar') {
                  return <Field key={fieldConfig.name} name={fieldConfig.name} render={({ input, meta }) => <MonthYearCalendarField input={input} name={fieldConfig.name} value={fieldValue} handleChange={handleChange} label={label} isRequired={fieldConfig.isRequired} testId={fieldConfig.testId} meta={meta} t={t} />} />
                }

                return <Field key={fieldConfig.name} name={fieldConfig.name} render={({ input, meta }) => <TextInputField input={input} name={fieldConfig.name} value={fieldValue} onChange={handleChange} label={label} placeholder={placeholder} isRequired={fieldConfig.isRequired} testId={fieldConfig.testId} meta={meta} t={t} />} />
              })}
            </form>
          )}
        />
        <div className="add-freight-rates-button">
          <ButtonComponent type="submit" disabled={isButtonDisabled} onClick={submitFreightRates} />
        </div>
      </div>

      <DialogModal {...showDialog} onDismiss={handleDismiss} onSuccess={handleSuccess} />
    </LayoutContainer>
  )
}

export default Loader(FreightRates)
